/**
 * recurringAccess — geteilte Zugriffslogik für die wiederkehrenden Rechnungen
 * (M3b, `/dashboard/wiederkehrend`).
 *
 * Single source of truth dafür, WER die Vorlagen-Verwaltung sehen und benutzen
 * darf. Wird von `config/navigation.ts` (Nav-Eintrag),
 * `routes/dashboardRoutePaths.ts` (Route-Registrierung) und
 * `pages/RecurringInvoicesPage.tsx` (Inhalts-Gate) gemeinsam genutzt — kein
 * Copy-Paste der Flag-/Allowlist-Bedingung. Muster:
 * `config/travelExpensesAccess.ts` / `config/receiptScanAccess.ts`.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-/Business-Account-UIDs sehen die
 *      Seite LIVE, unabhängig vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_RECURRING_ENABLED==='true'`.
 *
 * KEIN Tier-Term: die wiederkehrenden Rechnungen sind im Frontend tier-frei
 * (anders als UStVA/Belegscanner/Bank-Abgleich) — bei allgemeiner Freigabe gilt
 * daher schlicht das Env-Flag.
 *
 * Firebase-frei (nur `process.env`) — importierbar aus
 * `routes/dashboardRoutePaths.ts`/`config/navigation.ts`.
 *
 * @module config/recurringAccess
 */

/**
 * Beta-Erprobung wiederkehrende Rechnungen — hartkodiert (Muster
 * `RECEIPT_SCAN_BETA_UIDS`), für die kontrollierte Live-Erprobung durch Test-
 * (b3DRR1…) und Business-Account (70Tgnu…). Vor allgemeiner Freigabe wieder leeren.
 */
export const RECURRING_BETA_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
  '70TgnuHwmDNI2LovagtcxXtIflo2',
];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet (dieselbe Env-Var, die
 * `RecurringInvoicesPage.tsx` bisher direkt als `RECURRING_ENABLED` las).
 */
export const RECURRING_FLAG: boolean = process.env.REACT_APP_RECURRING_ENABLED === 'true';

/**
 * Route-Registrierungs-Gate (firebase-frei): die Route wird registriert, sobald
 * das Flag aktiv ist ODER die Beta-Allowlist nicht leer ist. Muster
 * `TRAVEL_EXPENSES_ROUTE_ENABLED`.
 */
export const RECURRING_ROUTE_ENABLED: boolean = RECURRING_FLAG || RECURRING_BETA_UIDS.length > 0;

/**
 * Entscheidet fail-closed, ob ein Nutzer die wiederkehrenden Rechnungen
 * sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist. Sonst `false`.
 */
export function canUseRecurring(uid: string | null | undefined): boolean {
  if (uid && RECURRING_BETA_UIDS.includes(uid)) {
    return true;
  }
  return RECURRING_FLAG;
}
